import React from 'react'
import { Tooltip } from 'src/components/Reusable/Tooltip'
import {
  TooltipOverlayType,
  TooltipPositions,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { useGrpcRevalidateByMethod } from 'src/components/async'
import { EvidenceGroup } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import {
  MappingsAddIcon,
  MappingsMinusIcon,
} from 'src/components/Reusable/Mappings/Mappings.styles'
import { useConfirmLinkControlsToEvidence } from 'src/pages/Evidence/evidence.hooks'
import { EvidenceManagementPromiseClient } from '@trustero/trustero-api-web/lib/evidencemgmt/evidencemgmt_grpc_web_pb'
import { useUnlinkControlsFromEvidence } from 'src/components/ModalForms/Evidence/useUnlinkControls'
import { AttachmentPromiseClient } from '@trustero/trustero-api-web/lib/attachment/attachment_grpc_web_pb'

export const EvidenceControlMappingUnlinkControlsButton = ({
  controls,
  evidence,
  tooltipText,
  isDisabled,
}: {
  controls: Control[]
  evidence: EvidenceGroup
  tooltipText: string
  isDisabled?: boolean
}): JSX.Element => {
  const canUnlinkControls = useCanPerformAction(
    CAPABILITY.CAN_LINK_EVIDENCE_CONTROLS,
  )
  const mutateFunc = useGrpcRevalidateByMethod()
  const unlinkControls = useUnlinkControlsFromEvidence({
    controlIds: controls.map((c) => c.getId()),
    evidenceId: evidence.getEvidenceId(),
    controlNames: controls.map((c) => `${c.getModelId()} ${c.getName()}`),
    caption: evidence.getCaption(),
    title: `${tooltipText}?`,
    mutateFunc: async () => {
      await mutateFunc(
        EvidenceManagementPromiseClient.prototype
          .getEvidenceToControlMappingInfo,
      )
      await mutateFunc(AttachmentPromiseClient.prototype.getEvidenceControls)
    },
  })
  return (
    <EvidenceControlMappingMinusButton
      onClick={unlinkControls}
      tooltipText={tooltipText}
      isDisabled={isDisabled || !canUnlinkControls}
    />
  )
}

export const EvidenceControlMappingLinkControlsButton = ({
  controls,
  evidence,
  tooltipText,
  isDisabled,
}: {
  controls: Control[]
  evidence: EvidenceGroup
  tooltipText: string
  isDisabled?: boolean
}): JSX.Element | null => {
  const canLinkControls = useCanPerformAction(
    CAPABILITY.CAN_LINK_EVIDENCE_CONTROLS,
  )
  const mutateFunc = useGrpcRevalidateByMethod()
  const linkControls: () => void = useConfirmLinkControlsToEvidence({
    evidenceId: evidence.getEvidenceId(),
    controlIds: controls.map((c) => c.getId()),
    controlNames: controls.map((c) => `${c.getModelId()} ${c.getName()}`),
    caption: evidence.getCaption(),
    title: `${tooltipText}?`,
    mutateFunc: async () => {
      await mutateFunc(
        EvidenceManagementPromiseClient.prototype
          .getEvidenceToControlMappingInfo,
      )
      await mutateFunc(AttachmentPromiseClient.prototype.getEvidenceControls)
    },
  })
  if (!evidence) return null
  return (
    <EvidenceControlMappingAddButton
      onClick={linkControls}
      tooltipText={tooltipText}
      isDisabled={isDisabled || !canLinkControls}
    />
  )
}

export const EvidenceControlMappingAddButton = ({
  onClick,
  tooltipText,
  isDisabled,
}: {
  onClick: () => void
  tooltipText: string
  isDisabled?: boolean
}): JSX.Element => {
  return (
    <Tooltip
      id="evidence-control-mapping-add-button"
      tooltipBody={tooltipText}
      placement={TooltipPositions.right}
      overlayType={TooltipOverlayType.popover}
      disabled={!tooltipText}
    >
      <MappingsAddIcon
        onClick={!isDisabled ? onClick : undefined}
        $disabled={isDisabled}
      />
    </Tooltip>
  )
}

export const EvidenceControlMappingMinusButton = ({
  onClick,
  tooltipText,
  isDisabled,
}: {
  onClick: () => void
  tooltipText: string
  isDisabled?: boolean
}): JSX.Element => {
  return (
    <Tooltip
      id="evidence-control-mapping-minus-button"
      tooltipBody={tooltipText}
      placement={TooltipPositions.right}
      overlayType={TooltipOverlayType.popover}
      disabled={!tooltipText}
    >
      <MappingsMinusIcon
        onClick={!isDisabled ? onClick : undefined}
        $disabled={isDisabled}
      />
    </Tooltip>
  )
}
